import { useState } from 'react'

import { Box, Text, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { Link, LinkRemove, People, Warning as WarningIcon } from '@factorialco/f0-react/icons/app'
import {
  CompetencyRoleLevelsDataCollectionDocument,
  CompetencyRoleLevelsDataCollectionTreeNodeFragment,
  CompetencyRoleLevelsPageCompetencyFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { CompetencyAssignRoleLevelsSidepanel } from 'modules/competencies/components/CompetencyAssignRoleLevelsSidepanel'
import { CompetencyLevelsAssignSidepanel } from 'modules/competencies/components/CompetencyLevelsAssignSidepanel'
import useUnassingCompetency from 'modules/competencies/hooks/useUnassingCompetency'
import { NODE_UUID_TYPES } from 'modules/job_catalog/lib/nodesUtils'

interface JobCatalogTreeNodeWithLevel extends CompetencyRoleLevelsDataCollectionTreeNodeFragment {
  level?: { id: string; name: string }
}

interface AssignSidepanelHeaderProps {
  competencyName: string
  jobName: string
}

const AssignSidepanelHeader = ({ competencyName, jobName }: AssignSidepanelHeaderProps) => {
  return (
    <Box gap="s4">
      <Text.Normal size="400" weight="semibold" color="textPrimary">
        {i18n.t('competencies_v3.levels.assign.header.title', {
          competency: competencyName,
        })}
      </Text.Normal>

      <Text.Normal size="300" color="textSecondary">
        {i18n.t('competencies_v3.levels.assign.header.description', {
          job: jobName,
        })}
      </Text.Normal>
    </Box>
  )
}

const findAttributeByCompetencyId = (
  attributes: CompetencyRoleLevelsDataCollectionTreeNodeFragment['attributes'],
  competencyId?: string
) => {
  return attributes?.find(
    (attr) => attr.attributeId === competencyId && attr.definedAt === NODE_UUID_TYPES.LEVEL
  )
}

const getExcludedRoleLevelsUuids = (
  records: CompetencyRoleLevelsDataCollectionTreeNodeFragment[],
  competency: CompetencyRoleLevelsPageCompetencyFragment
): string[] => {
  const hasCompetencyLevels = competency.competencyLevels.length > 0
  const competencyId = String(competency.id)

  if (!hasCompetencyLevels) {
    return records.map((record) => record.uuid)
  }

  return records.reduce<string[]>((acc, record) => {
    const competencyAttribute = findAttributeByCompetencyId(record.attributes, competencyId)
    if (competencyAttribute?.valueCompetency?.level) {
      acc.push(record.uuid)
    }
    return acc
  }, [])
}

interface Props {
  competency: CompetencyRoleLevelsPageCompetencyFragment
  canManageJobCatalogTree: boolean
}

const CompetencyRoleLevelsDataCollection = ({ competency, canManageJobCatalogTree }: Props) => {
  const [itemToEdit, setItemToEdit] = useState<JobCatalogTreeNodeWithLevel | null>(null)
  const unassingCompetency = useUnassingCompetency()
  const { openDialog } = useConfirmationDialog()
  const [showAssignSidepanel, setShowAssignSidepanel] = useState(false)

  const hasCompetencyLevels = competency.competencyLevels.length > 0

  const source = useGraphqlDataCollectionSource({
    totalItemSummary: true,
    document: CompetencyRoleLevelsDataCollectionDocument,
    mapData: (data) => data.jobCatalog.treeNodes || [],
    mapPagination: (data) => {
      return {
        total: data.jobCatalog.treeNodes.length ?? 0,
      }
    },
    mapVariables: () => ({ competencyId: String(competency?.id) }),
    itemActions: (item: CompetencyRoleLevelsDataCollectionTreeNodeFragment) => {
      const competencyAttribute = findAttributeByCompetencyId(
        item.attributes,
        String(competency?.id)
      )?.valueCompetency

      if (!canManageJobCatalogTree) return []

      return compact([
        hasCompetencyLevels && {
          label: i18n.t('competencies_v3.levels.role_levels_data_collection.actions.edit'),
          icon: People,
          onClick: () => {
            setItemToEdit({ ...item, level: competencyAttribute?.level ?? undefined })
          },
        },
        competencyAttribute
          ? {
              label: i18n.t('competencies_v3.levels.role_levels_data_collection.actions.unassign'),
              icon: LinkRemove,
              onClick: () => {
                openDialog({
                  title: i18n.t('competencies_v3.unassing_competency_level_modal.title'),
                  illustration: 'alert',
                  content: i18n.t('competencies_v3.unassing_competency_level_modal.description', {
                    competency_name: competency?.name,
                  }),
                  cancelLabel: i18n.t('common.cancel'),
                  confirmLabel: i18n.t('common.remove'),
                  onConfirm: () => unassingCompetency(item, competency.id),
                })
              },
              critical: true,
            }
          : null,
      ])
    },
    primaryActions: () =>
      canManageJobCatalogTree
        ? {
            label: i18n.t('competencies_v3.levels.role_levels_data_collection.actions.assign_jobs'),
            icon: Link,
            onClick: () => setShowAssignSidepanel(true),
          }
        : undefined,
    perPage: 500, // Setting a high limit because we cannot paginate the query as backend returns an InMemoryReadResult
  })

  const {
    data: { records },
  } = useDataCollectionData(source)

  const visualizations = [
    {
      type: 'table' as const,
      options: {
        columns: compact([
          {
            label: i18n.t(
              'competencies_v3.levels.role_levels_data_collection.columns.assigned_job'
            ),
            render: ({ ancestor, name }: CompetencyRoleLevelsDataCollectionTreeNodeFragment) => ({
              type: 'text' as const,
              value: [ancestor?.name, name].join(' '),
            }),
          },
          hasCompetencyLevels && {
            label: i18n.t(
              'competencies_v3.levels.role_levels_data_collection.columns.competency_level'
            ),
            render: (item: CompetencyRoleLevelsDataCollectionTreeNodeFragment) => {
              const { level } =
                findAttributeByCompetencyId(item.attributes, String(competency?.id))
                  ?.valueCompetency ?? {}

              if (level) {
                return {
                  type: 'tag' as const,
                  value: {
                    label: level.name,
                  },
                }
              }

              return {
                type: 'alertTag' as const,
                value: {
                  label: i18n.t('competencies_v3.levels.role_levels_data_collection.no_level'),
                  icon: WarningIcon,
                  level: 'critical',
                },
              }
            },
          },
        ]),
      },
    },
  ]

  const emptyStates = {
    'no-data': {
      title: i18n.t('competencies_v3.levels.role_levels_data_collection.empty_state.title'),
      description: hasCompetencyLevels
        ? i18n.t('competencies_v3.levels.assignation_form.description.with_levels')
        : i18n.t('competencies_v3.levels.assignation_form.description.without_levels'),
      actions: canManageJobCatalogTree
        ? [
            {
              label: i18n.t(
                'competencies_v3.levels.role_levels_data_collection.empty_state.action'
              ),
              icon: Link,
              variant: 'outline' as const,
              onClick: () => setShowAssignSidepanel(true),
            },
          ]
        : [],
    },
  }

  return (
    <>
      <OneDataCollection
        fullHeight
        source={source}
        visualizations={visualizations}
        emptyStates={emptyStates}
      />

      <CompetencyLevelsAssignSidepanel
        open={!!itemToEdit}
        competencyId={String(competency?.id)}
        jobCatalogNodeAssignments={
          itemToEdit && itemToEdit.name && itemToEdit.uuid
            ? [
                {
                  node: {
                    uuid: itemToEdit.uuid,
                    name: itemToEdit.name,
                  },
                  competencyLevelId: itemToEdit.level?.id,
                },
              ]
            : []
        }
        onClose={() => {
          setItemToEdit(null)
        }}
        title={i18n.t('competencies_v3.levels.assign.title_edit')}
        renderHeader={(headerCompetency) => (
          <AssignSidepanelHeader
            competencyName={headerCompetency.competency.name}
            jobName={[itemToEdit?.ancestor?.name, itemToEdit?.name].join(' ')}
          />
        )}
      />

      <CompetencyAssignRoleLevelsSidepanel
        open={showAssignSidepanel}
        competency={competency}
        competencyLevels={competency.competencyLevels}
        excludeRoleLevelsUuids={getExcludedRoleLevelsUuids(records, competency)}
        onClose={() => setShowAssignSidepanel(false)}
      />
    </>
  )
}

export default CompetencyRoleLevelsDataCollection
